import * as React from "react";
import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { fmtDate, fmtThroughInclusive } from "../utils/eligibilityDisplay";
import { deleteEligibilityPeriod } from "../services/eligibilityApi";

// Explicit confirmation dialog for HARD-DELETING a manually created eligibility period. Shows the
// franchise/com code and interval, and warns that the row is permanently removed — and, when it is the
// franchise's only period, that the franchise returns to no configuration (fail-open). On success calls
// onDeleted(); on failure keeps the dialog open with the backend error. The backend is authoritative
// about whether the row is deletable (source='manual').
export default function EligibilityDeletePeriodDialog({
  open,
  period,
  comCode,
  franchiseName,
  isFinalPeriod,
  onClose,
  onDeleted,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!period) return null;

  const throughInclusive = fmtThroughInclusive(period.enabled_through_date);

  const handleDelete = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await deleteEligibilityPeriod(period.enablement_period_id);
      onDeleted(`Eligibility period deleted for ${comCode}.`);
    } catch (e) {
      setError(e.message || "Delete failed.");
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete eligibility period</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          <strong>{comCode}</strong>
          {franchiseName ? ` · ${franchiseName}` : ""}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.5, mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">Enabled From</Typography>
          <Typography variant="body2">{fmtDate(period.enabled_from_date)}</Typography>
          <Typography variant="body2" color="text.secondary">Enabled Through</Typography>
          <Typography variant="body2">{throughInclusive ?? "Open-ended"}</Typography>
        </Box>
        <Alert severity="error" sx={{ mb: error ? 1.5 : 0 }}>
          This <strong>permanently removes</strong> the period from{" "}
          <code>app.lksi_franchise_enablement_period</code>. This cannot be undone.
          {isFinalPeriod && (
            <>
              {" "}
              Because it is this franchise&rsquo;s <strong>only</strong> eligibility period, the
              franchise will return to having <strong>no eligibility configuration</strong> and will{" "}
              <strong>fail open</strong> (the current <em>LK/SI Client</em> behaviour).
            </>
          )}
        </Alert>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleDelete} color="error" variant="contained" disabled={submitting}>
          {submitting ? "Deleting…" : "Delete period"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
